'use client';

import { useCallback, useEffect, useState } from 'react';

import { useTranslations } from 'next-intl';

import SignOutIcon from '@/assets/icons/account/signout.svg';
import TrendexLogo from '@/assets/icons/trendex-small.svg';
import WalletIcon from '@/assets/icons/wallet.svg';
import { Image } from '@/components/shared/Image';
import { KYCAlertBanner } from '@/components/shared/KYCAlertBanner';
import { LockedAccountBanner } from '@/components/shared/LockedAccountBanner';
import { P, H3, H4 } from '@/components/shared/Typography';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { Button } from '@/components/ui/button';
import { usePersonaKYC } from '@/hooks/usePersonaKYC';
import { useWalletConnection } from '@/hooks/useWalletConnection';
import { Link, useRouter } from '@/i18n/navigation';
import { useSignOut } from '@/lib/auth/useSignOut.client';
import { cn } from '@/lib/utils/cn';
import { formatAddress } from '@/lib/utils/formatAddress';
import { useGlobalData } from '@/providers/GlobalDataProvider';
import { C_Routes } from '@/ts/constants/C_Routes';
import { E_Routes } from '@/ts/enums/E_Routes';

function UserInfo() {
  const t = useTranslations();
  const { address, closeAccount, connector } = useWalletConnection();
  const user = useGlobalData('user');
  const signOut = useSignOut();
  const router = useRouter();
  const [hasMounted, setHasMounted] = useState(false);

  const walletIcons: Record<string, string> = {
    'io.metamask': '/assets/icons/wallets/metamask.svg',
    'com.coinbase.wallet': '/assets/icons/wallets/coinbase.svg',
    'me.rainbow': '/assets/icons/wallets/rainbow.svg',
    'io.rabby': '/assets/icons/wallets/rabby.svg',
    'com.trustwallet.app': '/assets/icons/wallets/trust.svg',
    walletConnect: '/assets/icons/wallets/walletConnect.svg'
  };

  const handleSignOut = useCallback(async () => {
    try {
      await signOut();
    } catch (e) {
      console.error(e);
    } finally {
      closeAccount();
      router.replace(C_Routes[E_Routes.HOME].link);
    }
  }, [signOut, closeAccount, router]);

  useEffect(() => {
    setHasMounted(true);
  }, []);

  const { needsKYC, approachingLimit } = usePersonaKYC();
  const isKYC = needsKYC || approachingLimit;

  return (
    <div className="flex flex-col">
      <div
        className={cn(
          'pb-px px-0 overflow-hidden w-full md:w-auto md:ml-0 mb-0 rounded-3xl rounded-t-none md:rounded-3xl bg-(image:--gradient-dark-to-light)',
          isKYC || user?.suspended
            ? 'pb-0 rounded-bl-none md:rounded-bl-none rounded-br-none md:rounded-br-none'
            : ''
        )}
      >
        <div
          className={cn(
            'p-6 md:p-8 flex flex-col md:flex-row items-unset md:items-center justify-between rounded-3xl rounded-t-none bg-(image:--gradient-black-to-gray)',
            isKYC || user?.suspended
              ? 'rounded-bl-none md:rounded-bl-none rounded-br-none md:rounded-br-none'
              : ''
          )}
        >
          <div className="md:hidden flex items-center gap-5 pb-6">
            <TrendexLogo />
            <H3>{t('profile.userInfo.profile')}</H3>
          </div>

          <div className="flex gap-5 flex-1 mb-6 p-0 md:p-6 md:mb-0 ">
            <UserAvatar
              src={user?.avatar?.url}
              alt="Avatar"
              width={90}
              height={90}
              className="min-w-22.5 h-22.5 rounded-full object-cover"
            />

            <div>
              <H4>{user?.nickname || '—'}</H4>
              <P className="text-sm font-bold text-muted-foreground mb-2">
                ID: {user?.uuid || '—'}
              </P>
              <Button variant="outline" asChild>
                <Link href={C_Routes[E_Routes.EDIT_PROFILE].link}>
                  {t('profile.userInfo.editProfile')}
                </Link>
              </Button>
            </div>
          </div>
          <div className="h-fit bg-card flex items-center justify-between flex-1 rounded-2xl w-full p-2.5 mb-2 md:mb-0">
            <div className="flex gap-3 items-center">
              <div className="relative h-10 w-10 overflow-hidden rounded-full bg-foreground flex items-center justify-center">
                {hasMounted &&
                (connector?.icon ||
                  walletIcons[connector?.id || ''] ||
                  (connector?.id.toLowerCase().includes('walletconnect') &&
                    walletIcons['walletConnect'])) ? (
                  <Image
                    src={
                      connector?.icon ||
                      walletIcons[connector?.id || ''] ||
                      walletIcons['walletConnect']
                    }
                    alt="Wallet"
                    className="object-contain p-1.5"
                  />
                ) : (
                  <WalletIcon className="w-6 h-6" />
                )}
              </div>
              <div>
                <P className="text-sm font-bold">
                  {connector?.name || 'Wallet'}
                </P>
                <P className="text-base">{formatAddress(address || '')}</P>
              </div>
            </div>

            <div
              className="flex items-center gap-2.5 px-3 py-1.5 cursor-pointer"
              onClick={handleSignOut}
            >
              <P className="text-sm font-bold cursor-pointer">
                {t('profile.userInfo.signOut')}
              </P>
              <SignOutIcon />
            </div>
          </div>
        </div>
      </div>
      <KYCAlertBanner className="rounded-bl-3xl rounded-br-3xl" show={isKYC} />
      <LockedAccountBanner className="rounded-bl-3xl rounded-br-3xl" />
    </div>
  );
}

export default UserInfo;
